/**
 * Dark Mode Composable
 *
 * Provides dark mode toggle with system preference detection and persistence.
 * Automatically syncs with localStorage and system color scheme preferences.
 */

import { ref, watch, onMounted, type Ref } from 'vue';

interface UseDarkModeReturn {
  // State
  isDark: Ref<boolean>;

  // Methods
  toggle: () => void;
  enable: () => void;
  disable: () => void;
  setFromSystem: () => void;
}

// Default to dark — check localStorage immediately (not in onMounted) to prevent flash
const savedTheme = typeof window !== 'undefined' ? localStorage.getItem('16p-theme') : null;
const isDark = ref(savedTheme ? savedTheme === 'dark' : true);

/**
 * Composable for managing dark mode
 *
 * @returns Reactive dark mode state and methods
 *
 * @example
 * ```ts
 * const { isDark, toggle, enable, disable } = useDarkMode();
 *
 * // Toggle dark mode
 * toggle();
 *
 * // Enable dark mode
 * enable();
 *
 * // Use system preference
 * setFromSystem();
 * ```
 */
export function useDarkMode(): UseDarkModeReturn {
  function toggle(): void {
    isDark.value = !isDark.value;
  }

  function enable(): void {
    isDark.value = true;
  }

  function disable(): void {
    isDark.value = false;
  }

  function setFromSystem(): void {
    // SSR safety check
    if (typeof window === 'undefined') {
      return;
    }
    isDark.value = window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  // Apply dark mode class to document
  watch(
    isDark,
    (value) => {
      // SSR safety check
      if (typeof window === 'undefined' || typeof document === 'undefined') {
        return;
      }

      if (value) {
        document.documentElement.classList.add('dark');
        document.documentElement.classList.remove('light');
        localStorage.setItem('16p-theme', 'dark');
      } else {
        document.documentElement.classList.remove('dark');
        document.documentElement.classList.add('light');
        localStorage.setItem('16p-theme', 'light');
      }
    },
    { immediate: true }
  );

  // Initialize on mount
  onMounted(() => {
    // SSR safety check
    if (typeof window === 'undefined') {
      return;
    }

    const savedTheme = localStorage.getItem('16p-theme');
    if (savedTheme) {
      isDark.value = savedTheme === 'dark';
    } else {
      // Default to dark since the app is dark-first
      isDark.value = true;
    }

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent): void => {
      if (!localStorage.getItem('16p-theme')) {
        isDark.value = e.matches;
      }
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    }
  });

  return {
    isDark,
    toggle,
    enable,
    disable,
    setFromSystem,
  };
}
